import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { HeroReveal, TextReveal } from '@/components/motion'
import { cn } from '@/lib/utils'

export interface LegalSection {
  id: string
  title: string
  body: React.ReactNode
}

/**
 * Prose rules for the legal body, applied to plain <p>, <ul>, <h3>, <a> and
 * <strong> so the page files carry only their copy. Body sits at 17px with a
 * 1.65 leading inside a 68ch measure; list markers are small accent squares.
 */
const prose = cn(
  '[&_p]:text-[1.0625rem] [&_p]:leading-[1.65] [&_p]:text-[var(--color-landing-gray-700)] dark:[&_p]:text-[var(--color-landing-gray-300)]',
  '[&_p+p]:mt-4 [&_p+ul]:mt-4 [&_ul+p]:mt-5 [&_p+div]:mt-6',
  '[&_ul]:space-y-2.5',
  '[&_li]:relative [&_li]:pl-6 [&_li]:text-[1.0625rem] [&_li]:leading-[1.6] [&_li]:text-[var(--color-landing-gray-700)] dark:[&_li]:text-[var(--color-landing-gray-300)]',
  "[&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[0.62em] [&_li]:before:h-1.5 [&_li]:before:w-1.5 [&_li]:before:bg-[var(--color-landing-accent)] [&_li]:before:content-['']",
  '[&_h3]:mt-9 [&_h3]:mb-3 [&_h3]:font-[family-name:var(--font-landing-subdisplay)] [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:tracking-[-0.01em] [&_h3]:text-[var(--color-landing-gray-900)] dark:[&_h3]:text-white',
  '[&_strong]:font-semibold [&_strong]:text-[var(--color-landing-gray-900)] dark:[&_strong]:text-white',
  '[&_a]:font-medium [&_a]:text-[var(--color-landing-gray-900)] [&_a]:underline [&_a]:decoration-[var(--color-landing-gray-300)] [&_a]:underline-offset-4 [&_a:hover]:text-[var(--color-landing-accent)] [&_a:hover]:decoration-[var(--color-landing-accent)] dark:[&_a]:text-white'
)

/** Square bordered contact card used at the end of each legal page. */
export function LegalContactCard({ email }: { email: string }) {
  return (
    <div className="border border-[var(--color-landing-gray-900)] p-6 dark:border-white/40">
      <p>
        <strong>Templatean</strong>
        <br />
        Email: <a href={`mailto:${email}`}>{email}</a>
      </p>
    </div>
  )
}

function useActiveSection(ids: string[]) {
  const [active, setActive] = useState(ids[0] ?? '')
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive(visible[0].target.id)
      },
      { rootMargin: '-15% 0px -70% 0px' }
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [ids])
  return active
}

function TocList({ sections, active }: { sections: LegalSection[]; active?: string }) {
  return (
    <ol className="space-y-0.5">
      {sections.map((section, i) => {
        const isActive = active === section.id
        return (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              aria-current={isActive ? 'location' : undefined}
              className={cn(
                'group flex items-baseline gap-3 py-1.5 text-[15px] leading-snug transition-colors duration-200',
                isActive
                  ? 'text-[var(--color-landing-gray-900)] dark:text-white'
                  : 'text-[var(--color-landing-gray-600)] hover:text-[var(--color-landing-accent)] dark:text-[var(--color-landing-gray-400)] dark:hover:text-white'
              )}
            >
              <span
                className={cn(
                  'w-6 shrink-0 text-xs font-semibold tabular-nums transition-colors duration-200',
                  isActive ? 'text-[var(--color-landing-accent)] dark:text-[#8b85ff]' : 'text-[var(--color-landing-gray-500)]'
                )}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className={cn(isActive && 'font-semibold')}>{section.title}</span>
            </a>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Long-form legal layout: highlighted H1 with a quote-bar lede and the
 * revision date, then a sticky numbered table of contents beside a 68ch
 * reading column. The contents collapse into a disclosure above the text
 * below lg. Ends on a full-width "see also" row to the sibling document.
 */
export function LegalPage({
  title,
  highlight,
  tone = 'blue',
  lede,
  lastUpdated,
  sections,
  related,
}: {
  title: string
  highlight: string
  tone?: 'blue' | 'purple'
  lede: string
  lastUpdated: string
  sections: LegalSection[]
  related: { label: string; href: string }
}) {
  const [ids] = useState(() => sections.map((s) => s.id))
  const active = useActiveSection(ids)

  return (
    <LandingLayout>
      <header className="landing-container border-b border-[var(--color-landing-gray-900)] pt-16 pb-14 dark:border-white/40 md:pt-24 lg:pb-20">
        <TextReveal
          as="h1"
          lines={[
            <>
              {title}{' '}
              <span className={cn('landing-highlight', tone === 'purple' && 'landing-highlight--secondary')}>{highlight}</span>
            </>,
          ]}
          className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white"
        />
        <HeroReveal delay={0.25}>
          <div className="mt-12 flex flex-col gap-8 lg:mt-16 lg:flex-row lg:items-end lg:justify-between lg:gap-24">
            <p className="max-w-[56ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
              {lede}
            </p>
            <dl className="flex gap-10 text-sm lg:text-right">
              <div>
                <dt className="text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">Last updated</dt>
                <dd className="mt-1 font-semibold text-[var(--color-landing-gray-900)] dark:text-white">{lastUpdated}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">Sections</dt>
                <dd className="mt-1 font-semibold tabular-nums text-[var(--color-landing-gray-900)] dark:text-white">{sections.length}</dd>
              </div>
            </dl>
          </div>
        </HeroReveal>
      </header>

      <div className="landing-container grid gap-10 py-14 lg:grid-cols-[minmax(14rem,1fr)_minmax(0,2.6fr)] lg:gap-20 lg:py-24">
        {/* Contents: sticky column on desktop, disclosure on smaller screens */}
        <nav aria-label="On this page" className="hidden lg:block">
          <div className="sticky top-28 max-h-[calc(100vh-9rem)] overflow-y-auto pr-2">
            <p className="mb-4 border-b border-[var(--color-landing-gray-200)] pb-3 text-sm font-semibold text-[var(--color-landing-gray-900)] dark:border-[var(--color-landing-gray-800)] dark:text-white">
              On this page
            </p>
            <TocList sections={sections} active={active} />
          </div>
        </nav>
        <details className="group border-y border-[var(--color-landing-gray-900)] dark:border-white/40 lg:hidden">
          <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-sm font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] dark:text-white [&::-webkit-details-marker]:hidden">
            On this page
            <ChevronDown className="h-5 w-5 transition-transform duration-200 group-open:rotate-180" aria-hidden />
          </summary>
          <nav aria-label="On this page" className="pb-5">
            <TocList sections={sections} />
          </nav>
        </details>

        <article className={cn('max-w-[68ch]', prose)}>
          {sections.map((section, i) => (
            <section
              key={section.id}
              id={section.id}
              aria-labelledby={`${section.id}-title`}
              className="scroll-mt-28 border-t border-[var(--color-landing-gray-200)] pt-10 pb-14 first:border-t-0 first:pt-0 dark:border-[var(--color-landing-gray-800)]"
            >
              <div className="mb-6 flex items-baseline gap-4">
                <span aria-hidden className="text-sm font-semibold tabular-nums text-[var(--color-landing-accent)] dark:text-[#8b85ff]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h2 id={`${section.id}-title`} className="landing-text-display-sm text-[var(--color-landing-gray-900)] dark:text-white">
                  {section.title}
                </h2>
              </div>
              {section.body}
            </section>
          ))}
        </article>
      </div>

      {/* See also: one full-width row to the sibling document */}
      <div className="landing-container pb-24 lg:pb-32">
        <Link
          to={related.href}
          className="group flex items-center justify-between gap-6 border-y border-[var(--color-landing-gray-900)] py-8 text-[var(--color-landing-gray-900)] transition-colors duration-200 hover:text-[var(--color-landing-accent)] dark:border-white/40 dark:text-white dark:hover:text-[#8b85ff] lg:py-10"
        >
          <span className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-8">
            <span className="text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">See also</span>
            <span className="landing-text-display-lg">{related.label}</span>
          </span>
          <ArrowRight className="h-8 w-8 shrink-0 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1.5" aria-hidden />
        </Link>
      </div>
    </LandingLayout>
  )
}
